// Sections PARTAGÉES entre moteurs de thème — pendant client de
// backend/services/theme_engines/shared_sections.py.
//
// Le serveur recopie dans le catalogue de chaque moteur HÔTE les sections
// de contenu de tous les autres moteurs, sous un type PRÉFIXÉ
// (« agence_hero » pour le "hero" d'agence_v1, « base_faq » pour la FAQ du
// moteur "default", « couture_gallery »…). Ici, le registre de sections de
// chaque moteur est composé de la même façon : ses propres composants, plus
// ceux des autres moteurs sous leur type préfixé — le composant d'origine
// rend la section, avec la palette de la boutique (toutes les couleurs
// passent par les jetons --c-*), et avec la typographie/les espacements de
// son moteur d'origine (ses `tokens.js`).
//
// Couche d'EXTENSION par thème : un moteur hôte peut remplacer ou envelopper
// le composant d'une section importée pour l'adapter à sa particularité —
// `SHARED_EXTENSIONS` de son registry.js, `{ type_préfixé: { Component,
// label? } }`. Le composant d'extension importe librement celui d'origine
// (`../agence_v1/sections/HeroSection`) pour l'envelopper. Le pendant
// serveur (textes par défaut, champs, pages) est `shared_overrides` du
// CATALOG du moteur (voir shared_sections.py).

// Préfixe de chaque moteur — miroir de `shared_prefix` côté serveur.
export const SHARED_PREFIXES = { default: "base", agence_v1: "agence", couture_v1: "couture" };

// Jamais partagés (miroir de NEVER_SHARED côté serveur + l'exclusion propre
// au moteur "default") : déjà communs, empruntés à l'identique, ou
// dispositif global plutôt que section de page.
const NEVER_SHARED = new Set(["product_grid", "checkout_form", "floating_contact", "privacy_content", "legal_content", "contact_modal"]);

const PREFIX_TO_ENGINE = Object.fromEntries(Object.entries(SHARED_PREFIXES).map(([e, p]) => [p, e]));

// « agence_hero » → { origin: "agence_v1", base: "hero" }, sinon null. Ne
// dit pas si la section existe : voir mergeSectionRegistry / getSharedOrigin.
export function parseSharedType(type = "") {
  const i = String(type).indexOf("_");
  if (i <= 0) return null;
  const origin = PREFIX_TO_ENGINE[type.slice(0, i)];
  return origin ? { origin, base: type.slice(i + 1) } : null;
}

export function sharedTypeName(origin, base) {
  return `${SHARED_PREFIXES[origin] || origin}_${base}`;
}

/**
 * Registre de sections COMPLET d'un moteur hôte : les sections des autres
 * moteurs (préfixées), recouvertes par les siennes, puis par ses extensions.
 *
 * @param host        nom du moteur hôte
 * @param registries  { [moteur]: SECTION_REGISTRY propre } pour tous les moteurs
 * @param extensions  SHARED_EXTENSIONS de l'hôte (facultatif)
 */
export function mergeSectionRegistry(host, registries, extensions = {}) {
  const merged = {};
  Object.entries(registries).forEach(([origin, registry]) => {
    if (origin === host) return;
    Object.entries(registry || {}).forEach(([type, entry]) => {
      if (NEVER_SHARED.has(type)) return;
      merged[sharedTypeName(origin, type)] = { ...entry, shared: { origin, type } };
    });
  });
  Object.assign(merged, registries[host] || {});
  Object.entries(extensions || {}).forEach(([type, ext]) => {
    if (!ext) return;
    const base = merged[type];
    merged[type] = { ...(base || {}), ...ext, shared: base?.shared || { origin: parseSharedType(type)?.origin, type: parseSharedType(type)?.base }, extended: true };
  });
  return merged;
}

// Provenance d'une section d'un registre composé, ou null pour une section
// propre au moteur.
export function getSharedOrigin(registry, type) {
  return registry?.[type]?.shared || null;
}
